import { useState, useEffect } from "react";
import { ExternalLink, BookOpen, TrendingUp, FileText, Video, ArrowRight, Clock } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const resources = [
  {
    icon: BookOpen,
    title: "Brewers Association - Draught Beer Quality",
    description: "Official quality guidelines from the Brewers Association for maintaining perfect draft beer.",
    url: "https://www.brewersassociation.org/educational-publications/draught-beer-quality-manual/",
    category: "Education",
  },
  {
    icon: TrendingUp,
    title: "National Restaurant Association",
    description: "Industry insights, trends, and best practices for running a successful bar operation.",
    url: "https://restaurant.org/",
    category: "Industry",
  },
  {
    icon: FileText,
    title: "FDA Food Code Guidelines",
    description: "Stay compliant with health and safety regulations for draft beverage systems.",
    url: "https://www.fda.gov/food/retail-food-protection/fda-food-code",
    category: "Compliance",
  },
  {
    icon: Video,
    title: "Draft Beer Dispensing Fundamentals",
    description: "Learn the science behind perfect pours and how system maintenance affects quality.",
    url: "https://www.micromatic.com/draft-beer-education",
    category: "Education",
  },
];

const RESOURCES_IMAGE = "https://images.unsplash.com/photo-1758518726775-70e538b0d46e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzB8MHwxfHNlYXJjaHwyfHxidXNpbmVzcyUyMHRhYmxldCUyMG1vZGVybiUyMG9mZmljZXxlbnwwfHx8fDE3NzU2MTAyMjV8MA&ixlib=rb-4.1.0&q=85";

const categoryColors = {
  "Education": "bg-green-500/20 text-green-400",
};

export default function ResourcesSection() {
  const [eduPosts, setEduPosts] = useState([]);
  const [selectedPost, setSelectedPost] = useState(null);

  useEffect(() => {
    const fetchEduPosts = async () => {
      try {
        const response = await axios.get(`${API_URL}/blog`);
        setEduPosts(response.data.filter(post => post.category === "Education"));
      } catch {
        toast.error("Unable to load education resources.");
      }
    };
    fetchEduPosts();
  }, []);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  if (selectedPost) {
    return (
      <section id="resources" className="py-24 bg-[#141414]" data-testid="resources-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <button
            onClick={() => setSelectedPost(null)}
            className="mb-8 text-[#D4AF37] hover:text-[#F5D061] flex items-center gap-2 transition-colors"
          >
            <ArrowRight className="w-4 h-4 rotate-180" />
            Back to Knowledge Center
          </button>
          <article className="bg-[#1A1A1A] border border-[#27272A] rounded-2xl overflow-hidden">
            {selectedPost.image_url && (
              <img src={selectedPost.image_url} alt={selectedPost.title} className="w-full h-64 object-cover" />
            )}
            <div className="p-8">
              <div className="flex items-center gap-4 mb-4">
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${categoryColors[selectedPost.category] || "bg-gray-500/20 text-gray-400"}`}>
                  {selectedPost.category}
                </span>
                <span className="text-[#A1A1AA] text-sm flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  {formatDate(selectedPost.created_at)}
                </span>
              </div>
              <h1 className="font-heading text-3xl text-white mb-4">{selectedPost.title}</h1>
              <p className="text-[#A1A1AA] text-sm mb-8">By {selectedPost.author}</p>
              <div className="prose prose-invert max-w-none">
                {selectedPost.content.split('\n').map((paragraph, index) => {
                  const key = `p-${index}`;
                  if (paragraph.startsWith('## ')) return <h2 key={key} className="font-heading text-2xl text-white mt-8 mb-4">{paragraph.replace('## ', '')}</h2>;
                  if (paragraph.startsWith('### ')) return <h3 key={key} className="font-heading text-xl text-white mt-6 mb-3">{paragraph.replace('### ', '')}</h3>;
                  if (paragraph.startsWith('**') && paragraph.endsWith('**')) return <p key={key} className="text-white font-semibold my-4">{paragraph.replace(/\*\*/g, '')}</p>;
                  if (paragraph.startsWith('- ') || paragraph.startsWith('* ')) return <li key={key} className="text-[#A1A1AA] ml-4">{paragraph.replace(/^[-*] /, '')}</li>;
                  if (paragraph.match(/^\d+\./)) return <li key={key} className="text-[#A1A1AA] ml-4 list-decimal">{paragraph.replace(/^\d+\.\s*/, '')}</li>;
                  if (paragraph.trim() === '') return <br key={key} />;
                  return <p key={key} className="text-[#A1A1AA] my-4 leading-relaxed">{paragraph}</p>;
                })}
              </div>
            </div>
          </article>
        </div>
      </section>
    );
  }

  return (
    <section id="resources" className="py-24 bg-[#141414]" data-testid="resources-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <p className="overline mb-4" data-testid="resources-overline">Knowledge Center</p>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-white mb-4" data-testid="resources-heading">
            Resources to Help Your{" "}
            <span className="gold-text">Business Thrive</span>
          </h2>
          <p className="text-[#A1A1AA] max-w-2xl mx-auto text-lg">
            We believe in empowering our customers with knowledge. These curated resources 
            from credible industry sources will help you run a more profitable bar.
          </p>
        </div>

        {/* Education Articles from Blog */}
        {eduPosts.length > 0 && (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            {eduPosts.map((post, index) => (
              <article
                key={post.id}
                className="bg-[#1A1A1A] border border-[#27272A] rounded-xl overflow-hidden card-hover cursor-pointer group"
                onClick={() => setSelectedPost(post)}
                data-testid={`edu-card-${index}`}
              >
                {post.image_url && (
                  <div className="relative h-40 overflow-hidden">
                    <img src={post.image_url} alt={post.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" loading="lazy" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A1A] to-transparent" />
                  </div>
                )}
                <div className="p-6">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-500/20 text-green-400">
                      {post.category}
                    </span>
                  </div>
                  <h3 className="font-medium text-white mb-2 line-clamp-2 group-hover:text-[#D4AF37] transition-colors">{post.title}</h3>
                  <p className="text-[#A1A1AA] text-sm line-clamp-2 mb-4">{post.excerpt}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-[#A1A1AA] text-xs">{formatDate(post.created_at)}</span>
                    <span className="text-[#D4AF37] text-sm font-medium flex items-center gap-1 group-hover:gap-2 transition-all">
                      Read <ArrowRight className="w-3 h-3" />
                    </span>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Featured Resource */}
          <div
            className="lg:row-span-2 relative rounded-2xl overflow-hidden"
            data-testid="resources-featured"
          >
            <img
              loading="lazy"
              src={RESOURCES_IMAGE}
              alt="Business resources"
              className="w-full h-full object-cover absolute inset-0"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/70 to-transparent" />
            <div className="relative h-full flex flex-col justify-end p-8">
              <p className="overline mb-2">Featured Resource</p>
              <h3 className="font-heading text-2xl text-white mb-4">
                Maximize Your Draft Beer Profits
              </h3>
              <p className="text-[#A1A1AA] mb-6">
                Did you know that improper line maintenance can cost you up to 20% of your keg yield? 
                Learn how regular cleaning directly impacts your bottom line.
              </p>
              <div className="flex flex-col gap-3">
                <div className="flex items-center text-sm">
                  <span className="w-2 h-2 bg-[#D4AF37] rounded-full mr-3" />
                  <span className="text-white">Reduce foam waste by 15-20%</span>
                </div>
                <div className="flex items-center text-sm">
                  <span className="w-2 h-2 bg-[#D4AF37] rounded-full mr-3" />
                  <span className="text-white">Extend equipment lifespan</span>
                </div>
                <div className="flex items-center text-sm">
                  <span className="w-2 h-2 bg-[#D4AF37] rounded-full mr-3" />
                  <span className="text-white">Improve customer satisfaction</span>
                </div>
              </div>
            </div>
          </div>

          {/* Resource Cards Grid */}
          <div
            className="lg:col-span-2 grid sm:grid-cols-2 gap-4"
          >
            {resources.map((resource) => (
              <a
                key={resource.title}
                href={resource.url}
                target="_blank"
                rel="noopener noreferrer"
                className="resource-card p-6 rounded-xl flex flex-col group cursor-pointer"
                data-testid={`resource-card-${resource.title}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="w-10 h-10 rounded-lg bg-[#D4AF37]/10 flex items-center justify-center">
                    <resource.icon className="w-5 h-5 text-[#D4AF37]" />
                  </div>
                  <span className="px-2 py-1 bg-[#1A1A1A] rounded-full text-xs text-[#A1A1AA]">
                    {resource.category}
                  </span>
                </div>
                <h3 className="font-medium text-white mb-2 group-hover:text-[#D4AF37] transition-colors">
                  {resource.title}
                </h3>
                <p className="text-[#A1A1AA] text-sm flex-1">{resource.description}</p>
                <div className="mt-4 flex items-center text-[#D4AF37] text-sm font-medium">
                  Visit Resource
                  <ExternalLink className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" />
                </div>
              </a>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
